import { supabase } from '../../lib/supabaseClient';
import type { Strings } from '../../i18n/strings';

// Typed calls to the platform-admin database functions
// (supabase/migrations/0009_platform_admin.sql) and the admin-ops edge
// function. Every function checks the caller's active role itself; nothing
// here reads a table directly, and nothing ever returns check-in results.

export type AppError = { code: string; message: string; detail?: string };
export type Result<T> = { data: T; error: null } | { data: null; error: AppError };

function toAppError(e: { message?: string; details?: string | null; code?: string } | null): AppError {
  const message = e?.message ?? 'unknown';
  const m = /^swipe:([a-z_]+)$/.exec(message);
  if (m) return { code: m[1], message, detail: e?.details ?? undefined };
  if (message === 'not authorized') return { code: 'not_authorized', message };
  return { code: 'unknown', message, detail: e?.details ?? undefined };
}

export async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<Result<T>> {
  const { data, error } = await supabase.rpc(fn, args ?? {});
  if (error) return { data: null, error: toAppError(error) };
  return { data: data as T, error: null };
}

// admin-ops answers { ok, ... } (and { ok: false, error, code }).
export async function ops<T>(action: string, body: Record<string, unknown>): Promise<Result<T>> {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean; error?: string; code?: string } & T>(
    'admin-ops',
    { body: { action, ...body } },
  );
  if (error) return { data: null, error: { code: 'ops_unreachable', message: error.message } };
  if (!data || !data.ok) {
    return { data: null, error: { code: data?.code ?? 'unknown', message: data?.error ?? 'unknown' } };
  }
  return { data: data as T, error: null };
}

/** The message to show for an error, in the person's language. */
export function errorText(t: Strings, e: AppError | null | undefined): string {
  if (!e) return '';
  const key = `err.${e.code}` as keyof Strings;
  const s = t[key];
  if (typeof s === 'string') return s;
  return t['err.generic'](e.detail || e.message);
}

// ── Shapes returned by the functions ───────────────────────────────────────

export type SchoolType = 'sandbox' | 'customer';
export type SchoolState = 'setting_up' | 'live' | 'suspended' | 'offboarded';
export type PaperworkStatus = 'not_started' | 'sent' | 'signed';
export type SchoolRole = 'school_admin' | 'head_of_school' | 'counsellor' | 'teacher';

export type SchoolRow = {
  id: string;
  name: string;
  slug: string;
  code: string;
  created_at: string;
  school_type: SchoolType;
  state: SchoolState;
  paperwork_status: PaperworkStatus;
  municipality_code: string | null;
  municipality_name: string | null;
  tier: string | null;
  staff_count: number;
  test_count: number;
  required_total: number;
  required_done: number;
  last_activity: string | null;
};

export type ChecklistKey =
  | 'safety_net'
  | 'paperwork'
  | 'profile'
  | 'classes'
  | 'staff'
  | 'teachers_assigned'
  | 'notice'
  | 'dpo'
  | 'safety_net_reviewed'
  | 'languages_reviewed'
  | 'wilma_link'
  | 'helpline'
  | 'dedicated';
export type ChecklistItem = {
  key: ChecklistKey;
  level: 'required' | 'warning';
  done: boolean;
  // Who completes it (0020): Zencera in a Sandbox school; otherwise the school admin.
  owner?: 'zencera' | 'school_admin' | 'head';
  // 0020: the FI/SV/EN safety-net texts still missing; languages not yet
  // checked by a native speaker; extra languages whose screen is a Draft.
  missing?: string[];
  unreviewed?: string[];
  draft?: string[];
  // 'dedicated' only (Tier B, 0013): the dedicated database request.
  status?: 'requested' | 'ready' | null;
  reference?: string | null;
  requested_at?: string | null;
  ready_at?: string | null;
};

export type SchoolDetail = {
  school: {
    id: string;
    name: string;
    code: string;
    school_type: SchoolType;
    state: SchoolState;
    municipality_code: string | null;
    municipality_name: string | null;
    official_number: string | null;
    stages: string[];
    default_language: 'fi' | 'sv' | 'en';
    tier: string | null;
    teacher_self_service: boolean;
    counsellor_whole_school: boolean;
    paperwork_status: PaperworkStatus;
    paperwork_signed_on: string | null;
    paperwork_signatory: string | null;
    paperwork_note?: string | null;
    is_demo?: boolean;
    safety_net: { fi?: string; sv?: string; en?: string } & Record<string, string | undefined>;
    safety_net_reviewed?: Record<string, { by: string; at: string }>;
    student_languages?: string[];
    // Help links (0022/0023): one shared value per school, not per language.
    wilma_url: string | null;
    helpline_name: string | null;
    helpline_contact: string | null;
    setup_steps: Record<string, { by: string; at: string; note: string | null }>;
    contract_reference: string | null;
    suspension_reason: string | null;
    suspension_message: string | null;
    state_changed_at: string;
    created_at: string;
    offboarding_ack: { name: string; on: string; note: string | null } | null;
    offboarded_at: string | null;
    offboard_after: string | null;
  };
  checklist: ChecklistItem[];
  in_support: boolean;
  counts: {
    staff: number;
    test_accounts: number;
    classes: number;
    classes_without_teacher: number;
    pending_approvals: number;
    queued_invites: number;
  };
  has_primary_head: boolean;
  school_admins: number;
  support_history: { started_at: string; ended_at: string | null; reason: string; by: string | null }[];
};

export type RoleGrantRow = {
  grant_id: string;
  role: SchoolRole;
  is_primary: boolean;
  status: 'active' | 'pending_approval';
  scope_grade_ids: string[] | null;
};
export type AssignmentRow = {
  id: string;
  kind: 'homeroom' | 'subject' | 'grade_lead';
  class_id: string | null;
  class_label: string | null;
  grade_id: string | null;
  grade_label: string | null;
};
export type Person = {
  id: string;
  display_name: string;
  email: string | null;
  is_test: boolean;
  test_expires_at: string | null;
  status: 'active' | 'deactivated' | 'erased';
  deactivated_at: string | null;
  reactivate_on: string | null;
  preferred_language: 'fi' | 'sv' | 'en' | null;
  last_sign_in_at: string | null;
  mfa_factors: number;
  other_schools: number;
  roles: RoleGrantRow[] | null;
  assignments: AssignmentRow[];
};
export type Invite = {
  id: string;
  email: string;
  display_name: string;
  roles: { role: SchoolRole }[];
  status: 'queued' | 'sent';
  language: string;
  created_at: string;
  expires_at: string | null;
};
export type PeopleData = { people: Person[]; invites: Invite[] };

export type ClassRow = {
  id: string;
  label: string;
  grade_id: string | null;
  section_id: string | null;
  expected_size: number | null;
  status: 'active' | 'archived';
  class_type: string;
  has_sessions: boolean;
  teachers: { assignment_id: string; user_id: string; name: string; kind: 'homeroom' | 'subject'; is_test: boolean }[];
};
export type ClassesData = {
  year: { id: string; label: string; starts_on: string; ends_on: string } | null;
  levels: string[];
  sections: { id: string; label: string; disabled: boolean; in_use: boolean }[];
  grades: {
    id: string;
    level: string;
    label: string;
    leads: { assignment_id: string; user_id: string; name: string; is_test: boolean }[];
  }[];
  classes: ClassRow[];
  teachers: { user_id: string; name: string; is_test: boolean }[];
};

export type SupportStatus =
  | { active: false }
  | {
      active: true;
      school_id: string;
      school_name: string;
      school_code: string;
      reason: string;
      started_at: string;
      expires_at: string;
    };

export type Approval = {
  id: string;
  kind: 'widen_role' | 'erase_person' | 'appoint_primary';
  school_id: string;
  school_name: string;
  school_code: string;
  subject_user: string | null;
  subject_name: string;
  subject_is_test: boolean | null;
  payload: {
    role?: SchoolRole;
    reason?: string;
    note?: string;
    route?: string;
    // appoint_primary (0016): the school's written instruction.
    instruction_from?: string;
    instruction_on?: string;
  };
  /** The school's current primary head, if any (shown for appointments). */
  current_primary?: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  requested_by_me: boolean;
  created_at: string;
  expires_at: string;
  signatory: string | null;
};

export type AuditRow = {
  id: number;
  school_id: string | null;
  school_code: string | null;
  occurred_at: string;
  actor: string;
  actor_label: string | null;
  acting_role: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target: string | null;
  details: Record<string, unknown>;
};
export type AuditPage = { rows: AuditRow[]; next_before: number | null; actions: string[] };

export type ArchiveRow = {
  id: number;
  kind: string;
  school_id: string | null;
  school_name: string | null;
  school_code: string | null;
  created_at: string;
  created_by: string;
  content: Record<string, unknown>;
};

export type AttentionItem =
  | { kind: 'paperwork'; school_id: string; school_name: string; status: PaperworkStatus }
  | { kind: 'setup'; school_id: string; school_name: string; done: number; total: number }
  | { kind: 'offboard_ready'; school_id: string; school_name: string; ack: boolean }
  | { kind: 'no_staff'; school_id: string; school_name: string; school_type: SchoolType }
  | { kind: 'no_school_admin'; school_id: string; school_name: string }
  | { kind: 'one_platform_admin' };

export type Overview = {
  schools: { total: number; sandbox: number; customer: number; live: number; setting_up: number; suspended: number };
  staff: number;
  test_accounts: number;
  tests_expiring: number;
  tests_expired: number;
  approvals: number;
  attention: AttentionItem[];
  support: SupportStatus;
};

export type PlatformSettings = {
  notification_email: string | null;
  email_enabled: boolean;
  require_mfa: boolean;
  my_session_aal: string;
  health: {
    db_size_bytes: number;
    db_limit_bytes: number;
    free_bytes: number;
    audit_log_bytes: number;
    audit_log_cap_bytes: number;
    records_archive_rows: number;
  };
  admins: {
    user_id: string;
    name: string;
    email: string | null;
    mfa_factors: number;
    last_sign_in_at: string | null;
    is_me: boolean;
  }[];
};

export type Notice = {
  id: string;
  kind: string;
  body: Record<string, unknown>;
  school_id: string | null;
  school_name: string | null;
  school_code: string | null;
  created_at: string;
  read_at: string | null;
};

// The role list the pickers build (see _validate_roles in 0009).
export type RoleSpec = {
  role: SchoolRole;
  is_primary?: boolean;
  scope_grade_ids?: string[] | null;
  classes?: { class_id: string; kind: 'homeroom' | 'subject' }[];
  grade_leads?: string[];
};

export const NATIONAL_LEVELS = [
  'vl1', 'vl2', 'vl3', 'vl4', 'vl5', 'vl6', 'vl7', 'vl8', 'vl9', 'lukio1', 'lukio2', 'lukio3', 'amm',
] as const;

/** Same labels as _grade_label() in the database. */
export function gradeLabel(level: string): string {
  if (/^vl[1-9]$/.test(level)) return level.slice(2);
  if (/^lukio[1-3]$/.test(level)) return `L${level.slice(5)}`;
  if (level === 'amm') return 'AMM';
  return level;
}

// ── Formatting and export helpers ──────────────────────────────────────────

export function fmtDate(locale: string, iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtDateTime(locale: string, iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${Math.round(n / 1e3)} kB`;
}

/** A spreadsheet-safe CSV cell: quoted, and never starting with = + - @ (spec C2). */
export function csvCell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function csvDownload(filename: string, header: string[], rows: string[][]) {
  const text = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  download(filename, `\uFEFF${text}`, 'text/csv;charset=utf-8');
}

export function download(filename: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
